import { Component, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatMenuModule } from '@angular/material/menu';
import { MatDialog } from '@angular/material/dialog';
import { ModalComponent } from '../../../utils/modal/modal.component';
import { FieldEditorDialogComponent } from '../field-editor-dialog/field-editor-dialog.component';
import { DEPENDENCY_FIELD_TYPES, FIELD_TYPES, FormField } from '../../../model/category-management-dto';

/**
 * Lista editable de campos del formulario. Se renderiza recursivamente para
 * las dependencias (campos que aparecen según la respuesta de otro campo).
 */
@Component({
  selector: 'app-field-list',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatCardModule,
    MatChipsModule,
    MatTooltipModule,
    MatMenuModule,
    FieldListComponent
  ],
  templateUrl: './field-list.component.html',
  styleUrl: './field-list.component.scss'
})
export class FieldListComponent {

  @Input() fields: FormField[] = [];
  @Input() nested = false;
  @Output() fieldsChange = new EventEmitter<FormField[]>();

  constructor(private dialog: MatDialog) {}

  typeLabel(type: string): string {
    return FIELD_TYPES.find(t => t.value === type)?.label || type;
  }

  typeIcon(type: string): string {
    switch (type) {
      case 'input_text': return 'short_text';
      case 'input_text_area': return 'notes';
      case 'select': return 'arrow_drop_down_circle';
      case 'multiselect': return 'checklist';
      case 'radio': return 'radio_button_checked';
      case 'checkbox': return 'check_box';
      case 'confirmation': return 'thumbs_up_down';
      case 'counter': return 'exposure_plus_1';
      case 'photos': return 'photo_library';
      default: return 'help_outline';
    }
  }

  canHaveDependencies(field: FormField): boolean {
    return DEPENDENCY_FIELD_TYPES.includes(field.type);
  }

  /** Respuestas posibles del campo que aún no tienen grupo de dependencias */
  availableAnswers(field: FormField): string[] {
    let answers: string[] = [];
    if (field.type === 'confirmation') {
      answers = [field.confirmText || 'Sí', field.cancelText || 'No'];
      if (field.neutralText) {
        answers.push(field.neutralText);
      }
    } else if (field.options?.length) {
      answers = field.options.map(o => o.option);
    }
    const existing = Object.keys(field.dependencies || {});
    return answers.filter(a => !existing.includes(a));
  }

  dependencyEntries(field: FormField): { answer: string; fields: FormField[] }[] {
    if (!field.dependencies) {
      return [];
    }
    return Object.entries(field.dependencies).map(([answer, fields]) => ({ answer, fields }));
  }

  addField(): void {
    const dialogRef = this.dialog.open(FieldEditorDialogComponent, {
      width: '640px',
      disableClose: true,
      data: { field: null }
    });
    dialogRef.afterClosed().subscribe((field: FormField | undefined) => {
      if (field) {
        this.fields.push(field);
        this.emitChange();
      }
    });
  }

  editField(index: number): void {
    const dialogRef = this.dialog.open(FieldEditorDialogComponent, {
      width: '640px',
      disableClose: true,
      data: { field: this.fields[index] }
    });
    dialogRef.afterClosed().subscribe((field: FormField | undefined) => {
      if (field) {
        this.fields[index] = field;
        this.emitChange();
      }
    });
  }

  deleteField(index: number): void {
    this.dialog.open(ModalComponent, {
      width: '460px',
      disableClose: true,
      data: {
        title: 'Confirmación',
        message: `¿Eliminar el campo "${this.fields[index].fieldTitle || this.fields[index].name}"?`,
        actions: [
          {
            label: 'Eliminar', callback: () => {
              this.fields.splice(index, 1);
              this.emitChange();
            }
          },
          { label: 'Cancelar', callback: () => {} }
        ]
      }
    });
  }

  moveField(index: number, direction: number): void {
    const target = index + direction;
    if (target < 0 || target >= this.fields.length) {
      return;
    }
    [this.fields[index], this.fields[target]] = [this.fields[target], this.fields[index]];
    this.emitChange();
  }

  addDependencyGroup(field: FormField, answer: string): void {
    if (!field.dependencies) {
      field.dependencies = {};
    }
    field.dependencies[answer] = [];
    if (field.uiRole !== 'parent') {
      field.uiRole = 'parent';
    }
    this.emitChange();
  }

  removeDependencyGroup(field: FormField, answer: string): void {
    this.dialog.open(ModalComponent, {
      width: '460px',
      disableClose: true,
      data: {
        title: 'Confirmación',
        message: `¿Eliminar los campos dependientes de la respuesta "${answer}"?`,
        actions: [
          {
            label: 'Eliminar', callback: () => {
              if (field.dependencies) {
                delete field.dependencies[answer];
                if (!Object.keys(field.dependencies).length) {
                  field.dependencies = null;
                  field.uiRole = 'standalone';
                }
              }
              this.emitChange();
            }
          },
          { label: 'Cancelar', callback: () => {} }
        ]
      }
    });
  }

  onNestedChange(): void {
    this.emitChange();
  }

  emitChange(): void {
    this.fieldsChange.emit(this.fields);
  }
}
